<template>
  <div class="broadcast-status">
    <div class="header-bar">
      <h2>Статус рассылок</h2>
      <button class="btn-refresh" @click="loadStatus" :disabled="loading">
        <i v-if="loading" class="bi bi-arrow-repeat spin"></i>
        <i v-else class="bi bi-arrow-clockwise"></i>
        {{ loading ? "Обновление..." : "Обновить" }}
      </button>
    </div>

    <div v-if="loading" class="loading">Загрузка...</div>

    <div v-else>
      <transition-group name="fade" tag="div">
        <div
            v-for="(progress, bot) in sortedBroadcasts"
            :key="bot"
            class="broadcast-card"
            :class="progress.status"
        >
          <div class="header">
            <h3>@{{ progress.bot }}</h3>
            <span class="status-label" :class="progress.status">
              {{ statusText(progress.status) }}
            </span>
          </div>

          <div class="progress-bar" v-if="progress.status !== 'queued'">
            <div
                class="progress"
                :style="{ width: progressPercent(progress) + '%' }"
            ></div>
          </div>

          <p class="stats" v-if="progress.status !== 'queued'">
            {{ progress.sent }} / {{ progress.total }} отправлено
            <span v-if="progress.status === 'done'">✅</span>
          </p>

          <p v-else class="queued-text">⏳ Ожидает своей очереди...</p>

          <p class="updated">Обновлено: {{ formatTime(progress.updated_at) }}</p>

          <button
              v-if="progress.status === 'done'"
              @click="deleteBroadcast(bot)"
              class="btn-delete"
          >
            🗑 Удалить
          </button>
        </div>
      </transition-group>

      <p v-if="Object.keys(broadcasts).length === 0" class="empty-state">
        Нет активных или ожидающих рассылок
      </p>
    </div>
  </div>
</template>

<script>
import { API_URL, WS_URL } from "@/config";
import axios from "axios";

export default {
  name: "BroadcastStatus",
  data() {
    return {
      broadcasts: {},
      loading: true,
      socket: null,
    };
  },
  computed: {
    sortedBroadcasts() {
      // сортируем: активные → в очереди → завершённые
      const order = { active: 1, queued: 2, done: 3 };
      return Object.fromEntries(
          Object.entries(this.broadcasts).sort(
              ([, a], [, b]) =>
                  (order[a.status] || 99) - (order[b.status] || 99)
          )
      );
    },
  },
  methods: {
    async loadStatus() {
      this.loading = true;
      try {
        const res = await axios.get(`${API_URL}/sender/status`);
        this.broadcasts = res.data;
      } catch (e) {
        console.error("Ошибка загрузки статуса:", e);
      } finally {
        this.loading = false;
      }
    },
    progressPercent(progress) {
      if (!progress.total) return 0;
      return Math.round((progress.sent / progress.total) * 100);
    },
    formatTime(isoString) {
      if (!isoString) return "-";
      const date = new Date(isoString);
      return date.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    statusText(status) {
      switch (status) {
        case "active":
          return "Активна";
        case "queued":
          return "В очереди";
        case "done":
          return "Завершена";
        default:
          return "Неизвестно";
      }
    },
    async deleteBroadcast(bot) {
      try {
        await axios.delete(`${API_URL}/sender/status/${bot}`);
        delete this.broadcasts[bot];
      } catch (e) {
        console.error("Ошибка удаления:", e);
      }
    },
    initWebSocket() {
      const wsUrl = `${WS_URL}/ws`;
      this.socket = new WebSocket(wsUrl);

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log(JSON.parse(event.data));
          if (data.bot) {
            this.broadcasts[data.bot] = data;
          }
        } catch (err) {
          console.error("Ошибка обработки WS:", err);
        }
      };

      this.socket.onclose = () => {
        console.warn("WebSocket закрыт");
      };
    },
  },
  mounted() {
    this.loadStatus();
    this.initWebSocket();
  },
  beforeUnmount() {
    if (this.socket) this.socket.close();
  },
};
</script>

<style scoped>
.broadcast-status {
  max-width: 900px;
  padding: 20px;
}

/* Заголовок */
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.header-bar h2 {
  font-size: 22px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Кнопка обновления */
.btn-refresh {
  background: #28b463;
  border: none;
  color: white;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s ease;
}
.btn-refresh:hover {
  background: #239b56;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Карточка рассылки */
.broadcast-card {
  background: #ffffff;
  border: 1px solid #e3e7ed;
  border-radius: 10px;
  padding: 15px 18px;
  margin-bottom: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
}
.broadcast-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07);
}

/* Разные статусы */
.broadcast-card.active { border-left: 5px solid #2ecc71; }
.broadcast-card.queued { border-left: 5px solid #f1c40f; }
.broadcast-card.done { border-left: 5px solid #95a5a6; }

/* Заголовок карточки */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.header h3 {
  font-size: 16px;
  font-weight: 600;
  color: #2d3436;
}
.status-label {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.status-label.active { color: #27ae60; }
.status-label.queued { color: #f1c40f; }
.status-label.done { color: #7f8c8d; }

/* Прогресс бар */
.progress-bar {
  background: #f1f3f5;
  border-radius: 6px;
  height: 10px;
  overflow: hidden;
  margin-bottom: 10px;
}
.progress {
  background: linear-gradient(90deg, #2ecc71, #27ae60);
  height: 100%;
  transition: width 0.3s ease;
}

/* Текст */
.stats {
  font-size: 14px;
  color: #2d3436;
  margin: 5px 0;
}
.queued-text {
  font-size: 14px;
  color: #a67f00;
  margin: 6px 0;
}
.updated {
  font-size: 12px;
  color: #95a5a6;
  margin-bottom: 10px;
}

/* Кнопка удаления */
.btn-delete {
  background: #e74c3c;
  border: none;
  color: white;
  font-size: 13px;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
}
.btn-delete:hover {
  background: #c0392b;
}

/* Анимация появления */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Пустое состояние */
.empty-state {
  text-align: center;
  font-size: 15px;
  color: #7f8c8d;
  margin-top: 25px;
}
</style>
